/**
 * Shared overnight-aware time-range helpers (the single client home of the guard).
 *
 * A slot whose end time-of-day is earlier than its start is an *overnight* slot
 * whose end falls on the next calendar day — but only when the start is at/after
 * noon (the evening-start guard). The guard blocks data-entry typos like
 * 08:00 -> 07:00 from being treated as a 23-hour overnight window.
 *
 *   valid_range(start, end):
 *     end == start  -> INVALID                       // zero-length
 *     end >  start  -> VALID  (same day)             // normal, unchanged
 *     end <  start  -> VALID iff start.hour >= 12    // ends next day (the guard)
 *
 * This contract is mirrored in two other repos that MUST stay in agreement
 * (change the guard in all three together):
 *   - api2:            app/Rules/SchedulerOvernightTimeRange.php (EVENING_START_GUARD)
 *   - scheduler-python: app/utils/time_ranges.py (require_evening_start default)
 *
 * All inputs are minutes-since-midnight (hour * 60 + minute).
 */

/** Earliest start hour (24h clock) for which end < start counts as overnight. */
export const EVENING_START_GUARD_HOUR = 12

const MINUTES_PER_DAY = 24 * 60

/**
 * Whether [startMinutes, endMinutes) is an overnight range (end on the next day).
 * Equal or end-after-start are same-day; an end-before-start before the evening
 * guard hour is a typo, not overnight.
 */
export function isOvernightTimeRange(
  startMinutes: number,
  endMinutes: number
): boolean {
  if (endMinutes >= startMinutes) {
    return false
  }
  if (Math.floor(startMinutes / 60) < EVENING_START_GUARD_HOUR) {
    return false
  }
  return true
}

/**
 * Whether [startMinutes, endMinutes) is a valid range: same-day (end > start) or
 * a guarded overnight range. Equal times (zero-length) are invalid.
 */
export function isValidTimeRange(
  startMinutes: number,
  endMinutes: number
): boolean {
  if (endMinutes > startMinutes) {
    return true
  }
  return isOvernightTimeRange(startMinutes, endMinutes)
}

/**
 * Duration in minutes, adding a full day for an overnight range (23:30 -> 00:45
 * = 75). A non-overnight end < start stays negative — it is never silently
 * widened into a ~23-hour slot.
 */
export function overnightDurationMinutes(
  startMinutes: number,
  endMinutes: number
): number {
  const end = isOvernightTimeRange(startMinutes, endMinutes)
    ? endMinutes + MINUTES_PER_DAY
    : endMinutes
  return end - startMinutes
}

/**
 * Format a slot's duration as a label, overnight-aware. `startTime`/`endTime`
 * are "HH:mm" or "HH:mm:ss". Returns `invalidPlaceholder` for missing or invalid
 * ranges (equal times or a pre-noon backwards typo) so a malformed/negative
 * value (e.g. "-1:00:00") is never shown mid-edit. The single home of the three
 * SchedulerWizard row duration renderers.
 */
export function formatSlotDuration(
  startTime: string,
  endTime: string,
  {
    withSeconds,
    invalidPlaceholder,
  }: { withSeconds: boolean; invalidPlaceholder: string }
): string {
  if (!startTime || !endTime) {
    return invalidPlaceholder
  }
  const [startH, startM] = startTime.split(':').map(Number)
  const [endH, endM] = endTime.split(':').map(Number)
  const startMinutes = startH * 60 + startM
  const endMinutes = endH * 60 + endM

  if (!isValidTimeRange(startMinutes, endMinutes)) {
    return invalidPlaceholder
  }

  const diffMinutes = overnightDurationMinutes(startMinutes, endMinutes)
  const hours = Math.floor(diffMinutes / 60)
    .toString()
    .padStart(2, '0')
  const minutes = (diffMinutes % 60).toString().padStart(2, '0')
  return withSeconds ? `${hours}:${minutes}:00` : `${hours}:${minutes}`
}
